import { useCallback, useEffect } from "react";
import { AlternativeProductGroup, useAlternativeProducts } from "./useAlternativeProducts";
import { useProductOptions } from "./useProductOptions";
import { useSupportedProductStartDate } from "./useSupportedProductStartDate";
import { useSelectedAlternativeProductGroupState } from "../components/bottom-menu-panels/alternative-products/hooks";
import { MenuIds, useMenus } from "./useMenus";

export function useOptionsMenu() {
	const {
		menuId,
		actions: { setMenu },
	} = useMenus();

	const { alternativeProductsGroups } = useAlternativeProducts();
	const { productOptionGroup } = useProductOptions();
	const supportedStartDate = useSupportedProductStartDate();

	const [selectedAlternativeProductGroup, setSelectedAlternativeProductGroup] =
		useSelectedAlternativeProductGroupState();

	const hasOptions =
		alternativeProductsGroups?.length || productOptionGroup || supportedStartDate;

	useEffect(() => {
		if (!hasOptions) {
			setMenu(MenuIds.Root);
		}
	}, [hasOptions, setMenu]);

	// Only show icons if all kinds of 'options' are configured with icons:
	// - alternative product groups
	// - product option group
	// - startDate option (omitted here because this option always has an icon)
	const showIconButtons =
		(!alternativeProductsGroups?.length ||
			alternativeProductsGroups.every((group) => group.icon)) &&
		(!productOptionGroup || !!productOptionGroup.icon);

	const handleStartDateClick = useCallback(() => {
		setMenu(menuId === MenuIds.DateSelection ? MenuIds.Options : MenuIds.DateSelection);
	}, [menuId, setMenu]);

	const handleAlternativeProductClick = useCallback(
		(group: AlternativeProductGroup) => {
			setSelectedAlternativeProductGroup(group);
			setMenu(
				menuId === MenuIds.AlternativeProducts &&
					selectedAlternativeProductGroup?.id === group.id
					? MenuIds.Options
					: MenuIds.AlternativeProducts,
			);
		},
		[menuId, selectedAlternativeProductGroup?.id, setMenu, setSelectedAlternativeProductGroup],
	);

	const handleProductOptionsClick = useCallback(() => {
		setMenu(menuId === MenuIds.ProductOptions ? MenuIds.Options : MenuIds.ProductOptions);
	}, [menuId, setMenu]);

	return {
		menuId,
		alternativeProductsGroups,
		productOptionGroup: productOptionGroup ?? undefined,
		supportedStartDate,
		showIconButtons,
		selectedAlternativeProductGroup: selectedAlternativeProductGroup as
			| AlternativeProductGroup
			| undefined,
		handleStartDateClick,
		handleAlternativeProductClick,
		handleProductOptionsClick,
	};
}
